import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  Box, Typography, Chip, IconButton, Button, CircularProgress,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import MicIcon from '@mui/icons-material/Mic';
import { canReviewOcr } from '../../utils/attachmentOcr';

import { Worker, Viewer, SpecialZoomLevel } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import mammoth from 'mammoth';

// ─── Image Preview ────────────────────────────────────────────────────────────
export const ImagePreview = React.memo(({ file, fitWidth = false }) => {
  const [src, setSrc] = useState('');

  useEffect(() => {
    if (!file) { setSrc(''); return; }
    const reader = new FileReader();
    reader.onload = (e) => setSrc(e.target.result);
    setSrc('');
    reader.readAsDataURL(file);
    return () => { reader.onload = null; reader.abort(); };
  }, [file]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: fitWidth ? 'flex-start' : 'center', width: '100%', height: fitWidth ? 'auto' : '100%', minHeight: fitWidth ? '100%' : undefined }}>
      {src
        ? <img src={src} alt="preview" style={fitWidth
          ? { display: 'block', width: '100%', height: 'auto' }
          : { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        : <CircularProgress size={24} />}
    </Box>
  );
});

// ─── PDF Preview ──────────────────────────────────────────────────────────────
export const PDFPreview = React.memo(({ file, page, onPageChange }) => {
  const [url, setUrl] = useState('');
  const viewer = useRef(null);
  const pageRef = useRef(page);
  pageRef.current = page;
  const navigation = useMemo(() => ({
    install: (functions) => { viewer.current = functions; },
    uninstall: () => { viewer.current = null; },
    onDocumentLoad: () => {
      if (pageRef.current !== undefined) viewer.current?.jumpToPage(pageRef.current);
    },
  }), []);
  useEffect(() => {
    if (page !== undefined) viewer.current?.jumpToPage(page);
  }, [page]);

  useEffect(() => {
    if (!file) return;
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  if (!url) return <CircularProgress size={24} />;

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
      <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
        <Viewer fileUrl={url} defaultScale={SpecialZoomLevel.PageWidth} plugins={[navigation]}
          onPageChange={event => onPageChange?.(event.currentPage)} />
      </Worker>
    </Box>
  );
});

// ─── Spreadsheet Preview ──────────────────────────────────────────────────────
const SpreadsheetPreview = React.memo(({ file }) => {
  const [tableData, setTableData] = useState({ headers: [], rows: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!file) return;
    const parse = async () => {
      try {
        if (file.name.endsWith('.csv')) {
          const text = await file.text();
          Papa.parse(text, {
            header: true,
            complete: (results) => {
              if (results.data.length > 0) {
                setTableData({ headers: Object.keys(results.data[0]), rows: results.data });
              }
              setLoading(false);
            },
          });
        } else {
          const buffer = await file.arrayBuffer();
          const wb = XLSX.read(buffer, { type: 'buffer' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const data = XLSX.utils.sheet_to_json(ws);
          if (data.length > 0) {
            setTableData({ headers: Object.keys(data[0]), rows: data });
          }
          setLoading(false);
        }
      } catch {
        setLoading(false);
      }
    };
    parse();
  }, [file]);

  if (loading) return <CircularProgress size={24} />;
  if (!tableData.rows.length) return <Typography color="text.secondary">No data found</Typography>;

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            {tableData.headers.map((h, i) => (
              <th key={i} style={{ padding: '8px', backgroundColor: '#f2f2f2', border: '1px solid #ddd', textAlign: 'left', position: 'sticky', top: 0 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableData.rows.map((row, ri) => (
            <tr key={ri}>
              {tableData.headers.map((h, ci) => (
                <td key={ci} style={{ padding: '8px', border: '1px solid #ddd' }}>{row[h]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
});

// ─── DOCX Preview ─────────────────────────────────────────────────────────────
export const DocxPreview = React.memo(({ file }) => {
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!file) return;
    file.arrayBuffer().then((buf) => {
      mammoth.convertToHtml({ arrayBuffer: buf }).then((result) => {
        setHtml(result.value);
        setLoading(false);
      }).catch(() => setLoading(false));
    });
  }, [file]);

  if (loading) return <CircularProgress size={24} />;

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'auto', p: 1 }}
      dangerouslySetInnerHTML={{ __html: html }} />
  );
});

// ─── Audio Preview ────────────────────────────────────────────────────────────
const AudioPreview = React.memo(({ file }) => {
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (!file) return;
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
      <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 600 }}>{file?.name}</Typography>
      {url && (
        <audio controls style={{ width: '100%', maxWidth: 480 }}>
          <source src={url} type={file?.type} />
          Your browser does not support the audio element.
        </audio>
      )}
    </Box>
  );
});

// ─── Video Preview ────────────────────────────────────────────────────────────
const VideoPreview = React.memo(({ file }) => {
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (!file) return;
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', height: '100%' }}>
      {url && (
        <video controls style={{ maxWidth: '100%', maxHeight: '100%' }}>
          <source src={url} type={file?.type} />
          Your browser does not support the video element.
        </video>
      )}
    </Box>
  );
});

// ─── Helpers ──────────────────────────────────────────────────────────────────
const MAX_MEDIA_BYTES = 100 * 1024 * 1024; // 100 MB

const AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'm4a', 'aac', 'ogg', 'flac', 'mpeg', 'mpga', 'wma']);
const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'mov', 'avi', 'mkv', 'mp4a']);
const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp']);

const getExtension = (filename = '') => filename.slice(filename.lastIndexOf('.') + 1).toLowerCase();

const isAudioFile = (filename) => AUDIO_EXTENSIONS.has(getExtension(filename));
const isVideoFile = (filename) => VIDEO_EXTENSIONS.has(getExtension(filename));

const typeColors = {
  Consultation: { bg: '#E5E7EB', text: '#6B7280' },
  Document: { bg: '#DCFCE7', text: '#16A34A' },
  Attachment: { bg: '#FEF3C7', text: '#D97706' },
};

const typeLabel = { Consultation: 'transcript', Document: 'document', Attachment: 'attachment' };

// ─── Main component ───────────────────────────────────────────────────────────
export default function DocumentPreviewModal({
  open,
  onClose,
  item,
  onOpenSideBySide,
  onDownload,
  onDelete,
  onEditTitle,
  onTranscribe,
  onOcr,
  retrieveAttachmentFile,
}) {
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [loadingAttachment, setLoadingAttachment] = useState(false);
  const [attachmentError, setAttachmentError] = useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Fetch attachment file when modal opens
  useEffect(() => {
    if (!open || !item || item.type !== 'Attachment') {
      setAttachmentFile(null);
      setAttachmentError('');
      return;
    }
    setAttachmentFile(null);
    setAttachmentError('');
    setLoadingAttachment(true);
    retrieveAttachmentFile(item.path, item.title)
      .then((file) => { setAttachmentFile(file); })
      .catch(() => { setAttachmentError('Failed to load file for preview.'); })
      .finally(() => setLoadingAttachment(false));
  }, [open, item]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = () => {
    setConfirmDeleteOpen(false);
    onDelete(item);
    onClose();
  };

  const handleEditTitle = () => {
    onEditTitle(item);
    onClose();
  };

  const handleTranscribe = () => {
    onTranscribe(item);
    onClose();
  };

  // ── Attachment content renderer ──────────────────────────────────────────
  const AttachmentContent = useMemo(() => {
    if (loadingAttachment) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress sx={{ color: '#29C0BB' }} />
        </Box>
      );
    }
    if (attachmentError) {
      return <Typography color="error" sx={{ p: 2 }}>{attachmentError}</Typography>;
    }
    if (!attachmentFile) return null;

    const ext = getExtension(attachmentFile.name);
    const tooBig = attachmentFile.size > MAX_MEDIA_BYTES;

    if (IMAGE_EXTENSIONS.has(ext)) {
      return <ImagePreview file={attachmentFile} />;
    }
    if (ext === 'pdf') {
      return <PDFPreview file={attachmentFile} />;
    }
    if (ext === 'csv' || ext === 'xlsx') {
      return <SpreadsheetPreview file={attachmentFile} />;
    }
    if (ext === 'docx') {
      return <DocxPreview file={attachmentFile} />;
    }
    if (ext === 'txt') {
      return (
        <Box sx={{ width: '100%', overflow: 'auto' }}>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: '0.85rem' }}>
            {/* read synchronously via state */}
            <TxtContent file={attachmentFile} />
          </pre>
        </Box>
      );
    }
    if (AUDIO_EXTENSIONS.has(ext)) {
      if (tooBig) {
        return (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Audio file is too large to preview (&gt;100 MB). Please download it to listen.</Typography>
          </Box>
        );
      }
      return <AudioPreview file={attachmentFile} />;
    }
    if (VIDEO_EXTENSIONS.has(ext)) {
      if (tooBig) {
        return (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Video file is too large to preview (&gt;100 MB). Please download it to view.</Typography>
          </Box>
        );
      }
      return <VideoPreview file={attachmentFile} />;
    }
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">Preview not available for this file type.</Typography>
      </Box>
    );
  }, [attachmentFile, loadingAttachment, attachmentError]);

  if (!item) return null;

  const color = typeColors[item.type] || typeColors.Attachment;
  const showOpenSideBySide = item.type === 'Consultation' || item.type === 'Document';
  const showDownload = item.type === 'Document' || item.type === 'Attachment';
  const showEditTitle = item.type === 'Consultation';
  const showTranscribe = item.type === 'Attachment' && isAudioFile(item.title);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: '12px', maxHeight: '90vh' } }}>
        {/* ── Header ── */}
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, pr: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.title || 'Untitled'}
            </Typography>
            <Chip
              label={typeLabel[item.type] || item.type.toLowerCase()}
              size="small"
              sx={{ fontSize: '0.65rem', height: 20, backgroundColor: color.bg, color: color.text, fontWeight: 600, flexShrink: 0 }}
            />
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: '#6B7280', flexShrink: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        {/* ── Preview Content ── */}
        <DialogContent sx={{ p: 2, minHeight: 400, display: 'flex', flexDirection: 'column' }}>
          {item.type === 'Consultation' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
              {/* Transcription */}
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Transcription
                </Typography>
                <Box sx={{ mt: 0.5, p: 1.5, backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', maxHeight: 280, overflowY: 'auto' }}>
                  {item.originalData?.transcription ? (
                    <Box
                      sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem', color: '#374151', lineHeight: 1.6 }}
                      dangerouslySetInnerHTML={{ __html: item.originalData.transcription }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ color: '#9CA3AF', fontStyle: 'italic' }}>No transcription available.</Typography>
                  )}
                </Box>
              </Box>
              {/* Remarks */}
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Remarks / Notes
                </Typography>
                <Box sx={{ mt: 0.5, p: 1.5, backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', maxHeight: 200, overflowY: 'auto' }}>
                  {item.originalData?.notes ? (
                    <Box
                      sx={{
                        fontSize: '0.8rem',
                        color: '#374151',
                        lineHeight: 1.6,
                        '& p': { margin: '0.25em 0' },
                        '& ul': { paddingLeft: '1.5em', margin: '0.25em 0', listStyleType: 'disc' },
                        '& ol': { paddingLeft: '1.5em', margin: '0.25em 0', listStyleType: 'decimal' },
                        '& li': { margin: '0.1em 0' },
                        '& strong': { fontWeight: 700 },
                        '& em': { fontStyle: 'italic' },
                        '& u': { textDecoration: 'underline' },
                        '& h1': { fontSize: '1.3em', fontWeight: 700, margin: '0.4em 0' },
                        '& h2': { fontSize: '1.1em', fontWeight: 700, margin: '0.4em 0' },
                        '& h3': { fontSize: '1em', fontWeight: 700, margin: '0.4em 0' },
                      }}
                      dangerouslySetInnerHTML={{ __html: item.originalData.notes }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ color: '#9CA3AF', fontStyle: 'italic' }}>No remarks available.</Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {item.type === 'Document' && (
            <Box sx={{ p: 1.5, backgroundColor: '#FAFAFA', border: '1px solid #E5E7EB', borderRadius: '8px', flex: 1, overflow: 'auto' }}>
              {item.originalData?.content ? (
                <Box
                  sx={{ 
                    fontSize: '0.85rem', 
                    lineHeight: 1.7, 
                    color: '#374151', 
                    '& h1,& h2,& h3': { margin: '0.5em 0' }, 
                    '& p': { margin: '0.3em 0' },
                    // Style nested tables inside generated documents
                    '& table': {
                      width: '100%',
                      borderCollapse: 'collapse',
                      margin: '16px 0',
                    },
                    '& th, & td': {
                      border: '1px solid #E5E7EB',
                      padding: '10px 12px',
                      textAlign: 'left',
                      fontSize: '0.85rem',
                    },
                    '& th': {
                      backgroundColor: '#F9FAFB',
                      fontWeight: 600,
                      color: '#111827',
                    },
                    '& tr:nth-of-type(even)': {
                      backgroundColor: '#F9FAFB',
                    },
                    '& tr:hover': {
                      backgroundColor: '#F3F4F6',
                    }
                  }}
                  dangerouslySetInnerHTML={{ __html: item.originalData.content }}
                />
              ) : (
                <Typography variant="body2" sx={{ color: '#9CA3AF', fontStyle: 'italic' }}>No content available.</Typography>
              )}
            </Box>
          )}

          {item.type === 'Attachment' && (
            <Box sx={{ flex: 1, minHeight: 300, display: 'flex', flexDirection: 'column' }}>
              {AttachmentContent}
            </Box>
          )}
        </DialogContent>

        <Divider />

        {/* ── Actions ── */}
        <DialogActions sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between' }}>
          {onOcr && canReviewOcr(item) && <Button variant="outlined" onClick={() => { onOcr(item); onClose(); }}>OCR / View text</Button>}
          {/* Left: destructive / secondary */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
              onClick={() => setConfirmDeleteOpen(true)}
              sx={{ borderColor: '#f44336', color: '#f44336', borderRadius: '8px', textTransform: 'none', fontSize: '0.75rem', fontWeight: 600, '&:hover': { borderColor: '#d32f2f', backgroundColor: '#fff5f5' } }}
            >
              Delete
            </Button>
            {/* {showEditTitle && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                onClick={handleEditTitle}
                sx={{ borderColor: '#6B7280', color: '#6B7280', borderRadius: '8px', textTransform: 'none', fontSize: '0.75rem', fontWeight: 600, '&:hover': { borderColor: '#374151', backgroundColor: '#F9FAFB' } }}
              >
                Edit Title
              </Button>
            )} */}
            {showTranscribe && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<MicIcon sx={{ fontSize: 16 }} />}
                onClick={handleTranscribe}
                sx={{ borderColor: '#29C0BB', color: '#29C0BB', borderRadius: '8px', textTransform: 'none', fontSize: '0.75rem', fontWeight: 600, '&:hover': { borderColor: '#1ca8a4', backgroundColor: '#F0FDFA' } }}
              >
                Transcribe
              </Button>
            )}
          </Box>

          {/* Right: primary actions */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {showDownload && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />}
                onClick={() => onDownload(item)}
                sx={{ borderColor: '#29C0BB', color: '#29C0BB', borderRadius: '8px', textTransform: 'none', fontSize: '0.75rem', fontWeight: 600, '&:hover': { borderColor: '#1ca8a4', backgroundColor: '#F0FDFA' } }}
              >
                Download
              </Button>
            )}
            {showOpenSideBySide && (
              <Button
                variant="contained"
                size="small"
                endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                onClick={() => { onOpenSideBySide(item); onClose(); }}
                sx={{ backgroundColor: '#29C0BB', '&:hover': { backgroundColor: '#1ca8a4' }, color: '#FFFFFF', borderRadius: '8px', textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
              >
                Edit
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation ── */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} maxWidth="xs" PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>Delete {item.type}?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.875rem' }}>
            Are you sure you want to delete "<strong>{item.title}</strong>"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setConfirmDeleteOpen(false)} size="small" sx={{ textTransform: 'none', color: '#6B7280', borderRadius: '8px' }}>Cancel</Button>
          <Button onClick={handleDelete} size="small" variant="contained" sx={{ textTransform: 'none', backgroundColor: '#f44336', '&:hover': { backgroundColor: '#d32f2f' }, borderRadius: '8px' }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── TxtContent (async text read inside pre) ──────────────────────────────────
function TxtContent({ file }) {
  const [text, setText] = useState('');
  useEffect(() => {
    if (!file) return;
    file.text().then(setText);
  }, [file]);
  return text;
}
